import type { CSVData, CSVRow, TextEntry, UpdatedEntry, DeduplicatedTextEntry } from './types';

// CSV Parsing and Generation
export function parseCSV(text: string): CSVData {
  if (!text || !text.trim()) return [];
  
  const lines = splitCSVIntoLines(text.trim());
  if (lines.length < 2) return [];
  
  const headers = parseCSVLine(lines[0]);
  const result: CSVData = [];
  
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    if (values.length === 0) continue; // Skip empty lines
    
    const obj: CSVRow = {};
    for (let j = 0; j < headers.length; j++) {
      obj[headers[j]] = values[j] || '';
    }
    result.push(obj);
  }
  
  return result;
}

// Helper function to properly split CSV into lines (handles quoted fields with newlines)
function splitCSVIntoLines(text: string): string[] {
  const lines: string[] = [];
  let currentLine = '';
  let inQuotes = false;
  
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const nextChar = text[i + 1];
    
    if (char === '"') {
      currentLine += char;
      // Check if this is an escaped quote
      if (inQuotes && nextChar === '"') {
        currentLine += nextChar;
        i++; // Skip the next quote
      } else {
        inQuotes = !inQuotes;
      }
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      // End of line (not within quotes)
      if (char === '\r' && nextChar === '\n') {
        i++; // Skip the \n in \r\n
      }
      if (currentLine.trim()) {
        lines.push(currentLine);
      }
      currentLine = '';
    } else {
      currentLine += char;
    }
  }
  
  // Don't forget the last line
  if (currentLine.trim()) {
    lines.push(currentLine);
  }
  
  return lines;
}

// Helper function to properly parse a CSV line (handles quoted fields)
function parseCSVLine(line: string): string[] {
  const values: string[] = [];
  let currentValue = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];
    
    if (char === '"') {
      if (!inQuotes) {
        // Starting a quoted field
        inQuotes = true;
      } else if (nextChar === '"') {
        // Escaped quote within a quoted field
        currentValue += '"';
        i++; // Skip the next quote
      } else {
        // Ending a quoted field
        inQuotes = false;
      }
    } else if (char === ',' && !inQuotes) {
      // Field separator (not within quotes)
      values.push(currentValue.trim());
      currentValue = '';
    } else {
      currentValue += char;
    }
  }
  
  // Don't forget the last value
  values.push(currentValue.trim());
  
  return values;
}

export function escapeCsvValue(value: string | null | undefined): string {
  if (value === null || value === undefined) return '';
  value = String(value);
  
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  
  return value;
}

export function generateCSV(csvData: CSVData, updatedEntries?: UpdatedEntry[]): string {
  if (!csvData || csvData.length === 0) return '';
  
  // Get headers from first object
  const headers = Object.keys(csvData[0]);
  const rows: CSVData = JSON.parse(JSON.stringify(csvData)); // Deep clone
  
  // Apply updates if provided
  if (updatedEntries) {
    updatedEntries.forEach(entry => {
      // Find row by Key column
      const row = rows.find(r => r['Key'] === entry.key || r['Key'] === entry.path);
      if (row) {
        // Find column that contains the language name
        const langColumn = entry.language ? findLanguageColumn(headers, entry.language) : undefined;
        if (langColumn) {
          row[langColumn] = entry.text;
        }
      }
    });
  }
  
  // Generate CSV string
  const csvRows: string[] = [];
  
  // Add headers
  csvRows.push(headers.map(h => escapeCsvValue(h)).join(','));
  
  // Add data rows
  rows.forEach(row => {
    const values = headers.map(header => escapeCsvValue(row[header]));
    csvRows.push(values.join(','));
  });
  
  return csvRows.join('\n');
}

export function generateSceneCSV(
  csvData: CSVData,
  sceneEntries: TextEntry[],
  updatedEntries?: UpdatedEntry[]
): string {
  if (!csvData || csvData.length === 0) return '';
  
  const scenePaths = new Set(sceneEntries.map(e => e.path));
  const headers = Object.keys(csvData[0]);
  
  // Filter rows that match scene paths
  const sceneRows = csvData.filter(row => scenePaths.has(row['Key']));
  
  if (sceneRows.length === 0) {
    console.warn('No matching rows found for current scene');
    return '';
  }
  
  const rows: CSVData = JSON.parse(JSON.stringify(sceneRows)); // Deep clone scene rows
  
  // Apply updates if provided
  if (updatedEntries) {
    updatedEntries.forEach(entry => {
      // Find row by Key column
      const row = rows.find(r => r['Key'] === entry.key || r['Key'] === entry.path);
      if (row) {
        // Find column that contains the language name
        const langColumn = entry.language ? findLanguageColumn(headers, entry.language) : undefined;
        if (langColumn) {
          row[langColumn] = entry.text;
        }
      }
    });
  }
  
  // Generate CSV string
  const csvRows: string[] = [];
  
  // Add headers
  csvRows.push(headers.map(h => escapeCsvValue(h)).join(','));
  
  // Add data rows
  rows.forEach(row => {
    const values = headers.map(header => escapeCsvValue(row[header]));
    csvRows.push(values.join(','));
  });

  return csvRows.join('\n');
}

function findLanguageColumn(headers: string[], language: string): string | undefined {
  const normalizedLanguage = language.toLowerCase();

  // Try exact match first
  const exact = headers.find(h => h.toLowerCase() === normalizedLanguage);
  if (exact) return exact;

  // Try contains match (e.g., "Language Name (Locale)")
  return headers.find(h => h.toLowerCase().includes(normalizedLanguage));
}

// File Download
export function downloadFile(filename: string, content: string, mimeType: string = 'text/csv'): void {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8;` });
  const link = document.createElement('a');
  
  if ('msSaveBlob' in navigator) {
    // IE 10+
    (navigator as any).msSaveBlob(blob, filename);
  } else {
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

// Search Highlighting
export function highlightSearchTerm(text: string, term: string): string {
  if (!text || !term) return text;
  
  const escapedTerm = escapeRegExp(term);
  const regex = new RegExp(`(${escapedTerm})`, 'gi');
  
  return text.replace(regex, '<span class="search-highlight">$1</span>');
}

export function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// HTML Escaping
export function escapeHtml(unsafe: string): string {
  if (typeof unsafe !== 'string') return unsafe;
  
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

// Debounce Function
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  
  return function(...args: Parameters<T>) {
    if (timeoutId) clearTimeout(timeoutId);
    
    timeoutId = setTimeout(() => {
      func(...args);
    }, wait);
  };
}

// Deep Clone
export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

// Get content from entry
export function getContent(entry: TextEntry): string {
  return entry.text || '';
}

// Format Date
export function formatDate(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleString();
}

// Check for Special Tags
export function hasSpecialTags(content: string): boolean {
  if (!content) return false;
  
  return (
    content.includes('[ref]') ||
    content.includes('[/ref]') ||
    content.includes('[indent=') ||
    content.includes('[/indent]')
  );
}

// Calculate Text Length for Sorting
export function calculateTextLength(entry: TextEntry): number {
  const content = getContent(entry);
  return content.length;
}

// Table Filtering Functions
export function getTableMetadata(unityData: any): { [tableName: string]: any } {
  if (!unityData?.tableInfos) return {};

  const metadata: { [tableName: string]: any } = {};

  // Unity sends tableInfos as an array, not an object
  if (Array.isArray(unityData.tableInfos)) {
    unityData.tableInfos.forEach((tableInfo: any) => {
      if (tableInfo?.tableName) {
        metadata[tableInfo.tableName] = {
          tableName: tableInfo.tableName,
          displayName: generateDisplayName(tableInfo.tableName),
          category: detectCategory(tableInfo.tableName),
          entryCount: tableInfo.totalEntries || 0,
          activeEntries: tableInfo.activeEntries || 0,
          tableGuid: tableInfo.tableGuid || ''
        };
      }
    });
  } else {
    // Fallback for object format (if Unity data structure changes)
    Object.keys(unityData.tableInfos).forEach((tableName: string) => {
      metadata[tableName] = {
        tableName,
        displayName: generateDisplayName(tableName),
        category: detectCategory(tableName),
        entryCount: 0 // Will be calculated when entries are processed
      };
    });
  }

  return metadata;
}

// Helper function to generate display names from table names
function generateDisplayName(tableName: string): string {
  return tableName
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before capital letters
    .replace(/[_-]/g, ' ') // Replace underscores and hyphens with spaces
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// Helper function to detect category from table name
function detectCategory(tableName: string): string {
  const lowerName = tableName.toLowerCase();
  if (lowerName.includes('ui') || lowerName.includes('interface') || lowerName.includes('button')) {
    return 'interface';
  }
  if (lowerName.includes('tutorial') || lowerName.includes('help') || lowerName.includes('guide')) {
    return 'tutorial';
  }
  if (lowerName.includes('system') || lowerName.includes('config') || lowerName.includes('setting')) {
    return 'system';
  }
  return 'content';
}

export function getEntriesByTable(entries: TextEntry[], tableMetadata: { [tableName: string]: any }): { [tableName: string]: TextEntry[] } {
  const entriesByTable: { [tableName: string]: TextEntry[] } = {};

  // Initialize empty arrays for all tables
  Object.keys(tableMetadata).forEach(tableName => {
    entriesByTable[tableName] = [];
  });

  // Group entries by their table reference
  entries.forEach(entry => {
    let assignedTable = null;

    // First, check if the entry has tableName directly (from Unity's TextEntry.tableName)
    if ((entry as any).tableName && (entry as any).tableName !== "") {
      assignedTable = (entry as any).tableName;
    }
    // Use tableReference if available (new Unity data structure)
    else if (entry.tableReference?.tableName) {
      assignedTable = entry.tableReference.tableName;
    }
    // Fallback: try to determine table from path or key patterns instead of assigning all to first table
    else {
      console.warn(`Entry ${entry.path || entry.id} has no table name - Unity may not be sending tableName properly`);

      // Try to infer table name from key patterns
      if ((entry as any).key) {
        // Look for existing table that might match this key pattern
        const tableNames = Object.keys(tableMetadata);

        // For now, assign to first table but log the issue
        if (tableNames.length > 0) {
          assignedTable = tableNames[0];
          console.warn(`Assigning entry ${entry.path || entry.id} to table ${assignedTable} as fallback`);
        }
      }
    }

    if (assignedTable && entriesByTable[assignedTable]) {
      entriesByTable[assignedTable].push(entry);
    } else if (assignedTable) {
      // If table doesn't exist in metadata, create it
      if (!entriesByTable[assignedTable]) {
        entriesByTable[assignedTable] = [];
      }
      entriesByTable[assignedTable].push(entry);
    }
  });

  return entriesByTable;
}

export function filterEntriesByTables(entries: TextEntry[], selectedTables: Set<string>, tableMetadata: { [tableName: string]: any }): TextEntry[] {
  if (selectedTables.size === 0) return [];

  const entriesByTable = getEntriesByTable(entries, tableMetadata);
  const filteredEntries: TextEntry[] = [];

  selectedTables.forEach(tableName => {
    if (entriesByTable[tableName]) {
      filteredEntries.push(...entriesByTable[tableName]);
    }
  });

  return filteredEntries;
}

export function getTableEntryCounts(entries: TextEntry[], tableMetadata: { [tableName: string]: any }): { [tableName: string]: number } {
  const entriesByTable = getEntriesByTable(entries, tableMetadata);
  const counts: { [tableName: string]: number } = {};

  Object.entries(entriesByTable).forEach(([tableName, tableEntries]) => {
    counts[tableName] = tableEntries.length;
  });

  return counts;
}

export function getCategoryColor(category: string): string {
  const colorMap: { [key: string]: string } = {
    content: '#4A90E2',
    interface: '#7B68EE',
    tutorial: '#50C878',
    system: '#95A5A6'
  };

  return colorMap[category] || '#95A5A6';
}

// Deduplication Functions

/**
 * Deduplicates text entries by (tableName, key) combination
 * Multiple GameObjects with the same localization reference are grouped into one entry
 * @param entries - Array of TextEntry objects from Unity
 * @returns Array of DeduplicatedTextEntry objects with grouped GameObject paths
 */
export function deduplicateTextEntries(entries: TextEntry[]): DeduplicatedTextEntry[] {
  const dedupeMap = new Map<string, DeduplicatedTextEntry>();

  entries.forEach(entry => {
    // Skip entries without a key (shouldn't happen but defensive)
    if (!entry.key || !entry.tableName) {
      console.warn('Entry missing key or tableName, skipping deduplication:', entry);
      return;
    }

    // Create unique deduplication ID
    const dedupeId = `${entry.tableName}|${entry.key}`;

    let dedupedEntry = dedupeMap.get(dedupeId);

    if (!dedupedEntry) {
      // First occurrence - create new deduplicated entry
      dedupedEntry = {
        dedupeId,
        key: entry.key,
        tableName: entry.tableName,
        tableGuid: entry.tableGuid,
        text: entry.templateValue || entry.text, // Use template value if available
        textLength: entry.textLength,
        sortTextLength: entry.sortTextLength,
        objectName: entry.objectName,
        gameObjectPaths: [entry.path],
        gameObjectIds: [entry.id],
        instanceCount: 1,
        hasActiveInstances: entry.isActive === true,
        hasInactiveInstances: entry.isActive === false,
        activeInstanceCount: entry.isActive ? 1 : 0,
        pathHighlighted: entry.pathHighlighted,
        contentHighlighted: entry.contentHighlighted,
        originalEntries: [entry],
        // Template vs Runtime values
        templateValue: entry.templateValue || entry.text,
        runtimeValues: entry.runtimeValue ? [entry.runtimeValue] : [],
        missingFromSheets: entry.missingFromSheets === true, // Start as true only if first entry is missing
        templateValuesByLanguage: entry.templateValuesByLanguage // Copy language-specific template values
      };

      dedupeMap.set(dedupeId, dedupedEntry);
    } else {
      // Subsequent occurrence - merge into existing
      dedupedEntry.gameObjectPaths.push(entry.path);
      dedupedEntry.gameObjectIds.push(entry.id);
      dedupedEntry.instanceCount++;

      if (entry.isActive) {
        dedupedEntry.hasActiveInstances = true;
        dedupedEntry.activeInstanceCount++;
      } else {
        dedupedEntry.hasInactiveInstances = true;
      }

      dedupedEntry.originalEntries.push(entry);

      // Collect runtime values from all deduplicated entries
      if (entry.runtimeValue) {
        dedupedEntry.runtimeValues!.push(entry.runtimeValue);
      }

      // Only keep missingFromSheets as true if ALL entries are missing
      // If any entry is NOT missing, set to false
      if (!entry.missingFromSheets) {
        dedupedEntry.missingFromSheets = false;
      }
    }
  });

  return Array.from(dedupeMap.values());
}

/**
 * Gets a display-friendly summary of GameObject paths for a deduplicated entry
 * Shows first N paths and indicates if there are more
 * @param dedupedEntry - The deduplicated entry containing multiple GameObject paths
 * @param maxPaths - Maximum number of paths to show (default: 3)
 * @returns Formatted string with path summary
 */
export function getPathSummary(dedupedEntry: DeduplicatedTextEntry, maxPaths: number = 3): string {
  const paths = dedupedEntry.gameObjectPaths;

  if (paths.length <= maxPaths) {
    return paths.join('\n');
  }

  const shown = paths.slice(0, maxPaths);
  const remaining = paths.length - maxPaths;
  return `${shown.join('\n')}\n... and ${remaining} more instance${remaining > 1 ? 's' : ''}`;
}

/**
 * Check if an entry is a deduplicated entry or a regular TextEntry
 * @param entry - Entry to check
 * @returns True if the entry is a DeduplicatedTextEntry
 */
export function isDeduplicatedEntry(entry: TextEntry | DeduplicatedTextEntry): entry is DeduplicatedTextEntry {
  return 'gameObjectPaths' in entry && Array.isArray((entry as DeduplicatedTextEntry).gameObjectPaths);
}

// Hierarchy Sorting Functions (DFS - Depth First Search)

/**
 * Core comparison function for two Unity hierarchy paths
 * Implements DFS ordering logic: parent-first, then siblings alphabetically
 * Unity path format: "SceneName > Parent > Child" (separator: " > ")
 *
 * @param a - First path
 * @param b - Second path
 * @returns Negative if a < b, positive if a > b, 0 if equal
 */
function comparePathsDFS(a: string, b: string): number {
  // Handle empty or null paths
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  // Split paths into segments using Unity's " > " separator
  const aParts = a.split(' > ').filter(Boolean);
  const bParts = b.split(' > ').filter(Boolean);

  // Compare segment by segment
  const minLength = Math.min(aParts.length, bParts.length);

  for (let i = 0; i < minLength; i++) {
    const comparison = aParts[i].localeCompare(bParts[i], undefined, {
      numeric: true,      // Handle numeric sorting ("Item2" < "Item10")
      sensitivity: 'base' // Case-insensitive comparison
    });

    if (comparison !== 0) {
      return comparison;
    }
  }

  // If all compared segments are equal, shorter path comes first (parent before child)
  // This ensures "Canvas > Panel" comes before "Canvas > Panel > Text"
  return aParts.length - bParts.length;
}

/**
 * Sorts mixed TextEntry and DeduplicatedTextEntry arrays by path in DFS order (reversed)
 * Handles both regular entries (with .path) and deduplicated entries (with .gameObjectPaths[])
 *
 * For deduplicated entries, uses the first path from gameObjectPaths array
 *
 * Performs DFS sort (parent-first, alphabetical), then reverses the entire array
 * This shows deepest/last elements first (bottom-up hierarchy view)
 *
 * @param entries - Array of TextEntry or DeduplicatedTextEntry objects
 * @returns Sorted array in reversed DFS traversal order (modifies in place and returns)
 */
export function sortEntriesByHierarchyDFS<T extends TextEntry | DeduplicatedTextEntry>(entries: T[]): T[] {
  if (!entries || entries.length === 0) return entries;

  // First, do normal DFS sort (parent-first, alphabetical)
  entries.sort((a, b) => {
    // Determine path based on entry type
    let aPath: string;
    let bPath: string;

    if (isDeduplicatedEntry(a)) {
      aPath = a.gameObjectPaths?.[0] || '';
    } else {
      aPath = a.path || '';
    }

    if (isDeduplicatedEntry(b)) {
      bPath = b.gameObjectPaths?.[0] || '';
    } else {
      bPath = b.path || '';
    }

    return comparePathsDFS(aPath, bPath);
  });

  // Then reverse the entire array to show bottom-up (deepest first)
  entries.reverse();

  return entries;
}

// Template Value Merging Functions

/**
 * Extract unique (tableName, key, language) tuples from Unity text entries
 * Used to determine what template values to fetch from Google Sheets
 * @param entries - Array of TextEntry objects from Unity
 * @param language - Language code to query (e.g., "Default Language")
 * @returns Array of request objects for fetchTemplateValuesByKeys
 */
export function extractUniqueTableKeys(
  entries: TextEntry[],
  language: string
): Array<{ tableName: string; key: string; language: string }> {
  // Use a Set to ensure uniqueness - composite key format: "tableName|key|language"
  const uniqueSet = new Set<string>();
  const result: Array<{ tableName: string; key: string; language: string }> = [];

  entries.forEach(entry => {
    // Filter out entries without required fields
    if (!entry.tableName || !entry.key) {
      console.warn('Entry missing tableName or key, skipping template fetch:', {
        id: entry.id,
        path: entry.path,
        tableName: entry.tableName,
        key: entry.key
      });
      return;
    }

    // Create composite key for deduplication
    const compositeKey = `${entry.tableName}|${entry.key}|${language}`;

    if (!uniqueSet.has(compositeKey)) {
      uniqueSet.add(compositeKey);
      result.push({
        tableName: entry.tableName,
        key: entry.key,
        language
      });
    }
  });

  console.log(`[extractUniqueTableKeys] Found ${result.length} unique (table, key, language) tuples from ${entries.length} entries`);

  // Log breakdown by table
  const tableBreakdown: { [tableName: string]: number } = {};
  result.forEach(({ tableName }) => {
    tableBreakdown[tableName] = (tableBreakdown[tableName] || 0) + 1;
  });
  console.log('[extractUniqueTableKeys] Breakdown by table:', tableBreakdown);

  return result;
}

/**
 * Merge Unity metadata with Google Sheets template values
 * Unity provides: id, path, key, tableName, runtimeValue (from TMP component)
 * Sheets provide: templateValue (with {variables} preserved)
 * Returns: Enriched TextEntry[] with both runtime and template values
 *
 * @param unityEntries - Original entries from Unity with runtime values
 * @param templateValuesMap - Map of composite keys to template values from Google Sheets
 *                            Key format: "tableName|key|language"
 * @param language - Language code used for the query
 * @returns Enriched array of TextEntry objects with template values merged in
 */
export function mergeUnityWithSheetValues(
  unityEntries: TextEntry[],
  templateValuesMap: Map<string, string>,
  language: string
): TextEntry[] {
  let foundCount = 0;
  let missingCount = 0;
  const missingKeys: string[] = [];

  const enrichedEntries = unityEntries.map(entry => {
    // Clone the entry to avoid mutating the original
    const enrichedEntry: TextEntry = { ...entry };

    // Store the original Unity text as runtimeValue (this has variables substituted)
    enrichedEntry.runtimeValue = entry.text || '';

    // Try to find template value from Google Sheets
    if (entry.tableName && entry.key) {
      const compositeKey = `${entry.tableName}|${entry.key}|${language}`;
      const templateValue = templateValuesMap.get(compositeKey);

      if (templateValue !== undefined) {
        // Found in Google Sheets - set template value
        enrichedEntry.templateValue = templateValue;
        enrichedEntry.text = templateValue;  // Primary display value
        enrichedEntry.missingFromSheets = false;
        foundCount++;
      } else {
        // Not found in Google Sheets - mark as missing
        enrichedEntry.missingFromSheets = true;
        enrichedEntry.text = enrichedEntry.runtimeValue;  // Fallback to runtime value
        missingCount++;
        missingKeys.push(`${entry.tableName}/${entry.key}`);
      }
    } else {
      // Entry missing tableName or key - keep as runtime value
      enrichedEntry.missingFromSheets = true;
      enrichedEntry.text = enrichedEntry.runtimeValue;
      missingCount++;

      if (!entry.tableName || !entry.key) {
        console.warn('[mergeUnityWithSheetValues] Entry missing tableName or key:', {
          id: entry.id,
          path: entry.path,
          tableName: entry.tableName,
          key: entry.key
        });
      }
    }

    return enrichedEntry;
  });

  // Log merge statistics
  console.log(`[mergeUnityWithSheetValues] Merge complete:`, {
    totalEntries: unityEntries.length,
    foundInSheets: foundCount,
    missingFromSheets: missingCount,
    templateValuesAvailable: templateValuesMap.size,
    language
  });

  if (missingCount > 0) {
    console.warn(`[mergeUnityWithSheetValues] ${missingCount} entries missing from Google Sheets:`, {
      sampleMissing: missingKeys.slice(0, 10),
      totalMissing: missingKeys.length,
      showingFirst: Math.min(10, missingKeys.length)
    });
  }

  return enrichedEntries;
}
